/* Finding a listing by typing at it.
 *
 * Guessing a symbol and being wrong is a quiet kind of wrong: 2498 prices
 * nothing, 2498.HK prices Hesai, and nothing on screen tells you which you got
 * until the position sits there with no mark. So the field asks the upstream
 * what a fragment means and shows the answers — name and exchange included,
 * because that is what distinguishes them. */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { searchCompanies, type SearchHit } from '../api/client';
import { TickerMark } from './TickerMark';
import { useStore } from '../state/store';

const stroke = {
  fill: 'none', stroke: 'currentColor', strokeWidth: 1.8,
  strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const,
};

function Magnifier() {
  return (
    <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
      <circle cx="8.5" cy="8.5" r="5.5" {...stroke} />
      <path d="M12.5 12.5 L17 17" {...stroke} />
    </svg>
  );
}

/** Long enough that typing a four-letter ticker is one request, short enough
    that the list feels like it is keeping up. */
const SETTLE_MS = 220;

export function SymbolSearch({ placeholder, label, onPick }: {
  placeholder: string;
  label: string;
  onPick: (hit: SearchHit) => void;
}) {
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<SearchHit[]>([]);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const inFlight = useRef<AbortController | null>(null);

  /* The marks for whatever came back.
   *
   * Picking a company off a list of eight is a different job from confirming a
   * symbol you already typed, and the logo is what makes it a glance rather
   * than a read — half of these rows are the same four letters on a different
   * exchange. Fetched after the results are on screen, never before: search has
   * to feel instant, and a second request that fills the tiles in a moment
   * later costs nothing that anybody notices. Names nobody ends up holding are
   * cleared out on the next load. */
  const { state, wantLogos } = useStore();
  const logos = useMemo(
    () => new Map(state.logos.filter((l) => l.dataUrl).map((l) => [l.symbol, l.dataUrl!])),
    [state.logos],
  );

  useEffect(() => {
    const q = query.trim();
    if (q.length < 1) { setHits([]); setNote(null); return; }

    const timer = window.setTimeout(() => {
      inFlight.current?.abort();
      const ctrl = new AbortController();
      inFlight.current = ctrl;
      setBusy(true);
      void searchCompanies(q, ctrl.signal)
        .then((rows) => {
          if (ctrl.signal.aborted) return;
          setHits(rows);
          setNote(rows.length === 0 ? `Nothing came back for "${q}".` : null);
        })
        .catch(() => {
          if (!ctrl.signal.aborted) setNote('Could not reach the search.');
        })
        .finally(() => { if (!ctrl.signal.aborted) setBusy(false); });
    }, SETTLE_MS);

    return () => window.clearTimeout(timer);
  }, [query]);

  /* Read from a ref, not closed over. The store rebuilds its api object on
     every state change, so depending on the function itself would re-run this
     on every render rather than when the results change — the same reason
     `quotesRef` exists in the store. */
  const wantRef = useRef(wantLogos);
  wantRef.current = wantLogos;

  const hitKey = hits.map((h) => h.symbol).join(',');
  useEffect(() => {
    if (hitKey === '') return;
    wantRef.current(hitKey.split(','));
  }, [hitKey]);

  const take = useCallback((hit: SearchHit) => {
    onPick(hit);
    setQuery('');
    setHits([]);
    setNote(null);
  }, [onPick]);

  return (
    <div className="field" style={{ marginBottom: 10 }}>
      <label htmlFor={`search-${label}`}>{label}</label>
      <div className="search-row" style={{ marginBottom: 0 }}>
        <span className="search-icon" aria-hidden="true"><Magnifier /></span>
        <input
          id={`search-${label}`}
          className="input"
          type="search"
          autoCapitalize="characters"
          autoComplete="off"
          placeholder={placeholder}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          // Enter takes the first answer, which is what the list is sorted for.
          onKeyDown={(e) => { if (e.key === 'Enter' && hits[0]) { e.preventDefault(); take(hits[0]); } }}
        />
      </div>

      {busy && hits.length === 0 ? (
        <div className="dim" style={{ fontSize: 11, marginTop: 8 }}>Looking…</div>
      ) : null}

      {hits.length > 0 ? (
        <div className="suggestions" role="listbox" aria-label="Matching companies">
          {hits.map((h) => (
            <button key={`${h.symbol}-${h.exchange}`} className="suggestion" role="option"
                    aria-selected="false" onClick={() => take(h)}>
              <TickerMark ticker={h.symbol} color="var(--dim2)" size={22}
                          logo={logos.get(h.symbol)} />
              <span className="suggestion-symbol num">{h.symbol}</span>
              <span className="suggestion-name">{h.name}</span>
              <span className="suggestion-where">{h.exchange}</span>
            </button>
          ))}
        </div>
      ) : null}

      {note ? <div className="dim" style={{ fontSize: 11, marginTop: 8 }}>{note}</div> : null}
    </div>
  );
}
